/**
 * Unit Zone Schedule
 * - display a list of thermostat schedules for all unit zones
 * - sort by schedule types like 'Custom', 'SameEveryDay' or 'Weekday/Weekend'
 */
import React, { useState } from 'react';
/* MUI */
import { Box, Card, CardHeader, Typography, Fade } from '@mui/material';
/* HELPERS */
import { WdTab, WdTabs } from './weekday-tabs';
/* TYPES */
import { DayOfWeek } from '../../types/schedule-types';
import {
  EnumScheduleType,
  ThermostatSchedule,
} from '../../../../../../../types/generated-types';
import { ThermostatScheduleTable } from './thermostat-schedule-table';
import { daysToDisplay } from '../helpers/days-to-display';
import { useIsSmallScreen } from '../../../../../../system/SystemMediaQueriesProvider';

const dayOfWeekLabels: Record<DayOfWeek, string> = {
  [DayOfWeek.SU]: 'SU',
  [DayOfWeek.MO]: 'MO',
  [DayOfWeek.TU]: 'TU',
  [DayOfWeek.WE]: 'WE',
  [DayOfWeek.TH]: 'TH',
  [DayOfWeek.FR]: 'FR',
  [DayOfWeek.SA]: 'SA',
};
const label: string[] = ['Weekday', 'Weekend'];

interface ThermostatScheduleViewProps {
  scheduleType: EnumScheduleType;
  scheduleValue: ThermostatSchedule['value'];
  thermostatName: string;
  showPlaceholder?: boolean;
}

export function ThermostatScheduleView(props: ThermostatScheduleViewProps) {
  const { thermostatName, scheduleValue, scheduleType, showPlaceholder } =
    props;
  const selectedScheduleType = scheduleType ?? EnumScheduleType.SameEveryDay;
  const [selectedDay, setSelectedDay] = useState(
    selectedScheduleType === EnumScheduleType.WeekdayWeekend
      ? DayOfWeek.MO
      : DayOfWeek.SU,
  );
  const isSmall = useIsSmallScreen();

  const dailySchedule = scheduleValue[selectedDay] ?? {};

  // Get tab labels by schedule type
  // Example: If schedule type is 'SameEveryDay' then only display one tab label 'Every day'
  function getTabLabel(dayOfWeek: DayOfWeek): string {
    if (selectedScheduleType === EnumScheduleType.SameEveryDay) {
      return 'Everyday';
    } else {
      return dayOfWeekLabels[dayOfWeek];
    }
  }

  // View change handler
  function handleViewChange(event: React.SyntheticEvent, newValue: DayOfWeek) {
    setSelectedDay(newValue);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Card
        sx={{
          background: 'inherit',
          margin: isSmall ? '0px' : '0px 20px',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          alignItems: 'stretch',
        }}
        elevation={4}
      >
        <CardHeader
          title={
            <Typography variant="h4" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
              {thermostatName ?? 'N/A'}
            </Typography>
          }
        />

        <>
          <WdTabs
            variant="fullWidth"
            value={selectedDay}
            onChange={handleViewChange}
          >
            {daysToDisplay(selectedScheduleType).map((dayOfWeek, index) => {
              return (
                <WdTab
                  key={dayOfWeek}
                  value={dayOfWeek}
                  label={
                    daysToDisplay(selectedScheduleType).length === 2 &&
                    selectedScheduleType === EnumScheduleType.WeekdayWeekend
                      ? label[index]
                      : getTabLabel(dayOfWeek)
                  }
                />
              );
            })}
          </WdTabs>
          <ThermostatScheduleTable dailySchedule={dailySchedule} />
        </>
        <br />
      </Card>
    </Box>
  );
}
